<template>
	<div>
		<p>
			This tutorial will cover some Blender export settings for the FBX file format, as well as some import settings on the Unity side of things. If you're using a different modelling 
			program than Blender to make your characters, then you will need to find other resources for exporting Unity-friendly models for your software.
		</p>
        
		<p>
			In Blender, select your character's rig and mesh parts and go to File -> Export -> FBX. Here are the FBX export settings that I use for my characters:
		</p>
        
		<b-img src="/images/importer/model-export-import/export-settings.jpg"></b-img>
		<p>
			Important things to note:
			<ul>
				<li>
                    Turn "Limit to Selected Objects" on, or else you'll get the whole scene exported.
				</li>
				<li>
                    Under Transform, change "Apply Scalings" to "FBX Units Scale", or else your object will get imported into unity with a scale of 100 instead of 1, which can cause some issues.
				</li>
				<li>
                    Under Armature, turn on the "Only Deform Bones" flag, which will remove any extra bones from your rig. This one isn't necessary, and you may not want to do this if you've got 
					non-deforming bones that you want to keep in your rig. I always set this on to remove any IK-related bones and controls that are not used on the Unity side.
				</li>
				<li>
                    Since this is just the model in a T-Pose, you can uncheck the "Bake Animation" section so it doesn't export any animation data that doesn't need to be included. Expression animations
					can be exported as individual .fbx files later, or you could include them in this export if you're more comfortable with that workflow - I prefer to keep them separate since it's easier
					to swap out if a change needs to be made to just one animation.
				</li>
			</ul>
		</p>

		<p>
			Export the model and copy the generated FBX file into your Character Importer Unity project. You should see the file appear in your Project view - there are a few import settings that you may want 
			to change from their defaults, so click on that file and check the Inspector window.
		</p>
		
		<b-img src="/images/importer/model-export-import/import-settings-rig.jpg"></b-img>
		<p>
			Anthro characters are required to set up a Unity Avatar in order for certain configuration and animation functions to work - this is used later with auto-populating
			some of the fields that the Character Importer uses. Non-Anthro characters should also set up Unity Avatar, in order to set up the Hand/Finger bones to be able to curl
			the fingers. For Animation Type, change this to Humanoid, and for Avatar Definition select Create From This Model, and then Apply. Next, click on the Configure button
			in this same window to edit the avatar.
		</p>
        
		<b-img src="/images/importer/model-export-import/avatar-setup.jpg"></b-img>
		<p>
			Unity fills out the fields here by guessing the correct values based on their names or positions, so they may or may not be correct. You can drag and drop the correct bones from the Hierarchy
			window into the fields in the Inspector to fix them as needed.
		</p>

		<p>
			There are multiple tabs for the Avatar's Body, Head, and Hands which all have fields that are used by the Importer, so be sure to check those for anything that looks out of place. Once everything 
			looks good, hit Apply and Done to exit the Avatar mode.
		</p>
        
		<b-img src="/images/importer/model-export-import/import-settings-model.jpg"></b-img>
		<p>
			With the fbx file still selected, there are some more import settings to change on the Model tab. For the most part, things can be left at their defaults, but one thing you 
			may want to do is enable the Read/Write flag, which is required for a few things like the Softbody module, and generally doesn't hurt anything to have enabled. You may also want to change 
			the Blend Shape Normals setting from the default "Calculate", to "Import" instead. Otherwise, Unity will generate new normals for every blendshape in the model - if you've got a lot of 
			blendshapes for things like facial animation, this can cause the filesize for your character package to get massively increased, and can also cause some undesirable effects depending 
			on the default smoothness settings. With those settings changed, click Apply and you're all set.
		</p>
        
		<b-img src="/images/importer/installation/setup-window.jpg"></b-img>
		<p>
			Now, in the title menu in Unity there is a "FarmD" menu with one option "Character Import Setup". Select this and a window will appear for the initial setup of the project. If this is the
			first time using the Character Importer, you'll want to click the Set Physics Properties button here, which will initialize the project's layers and collision matrix to match what the 
			game uses. That way, if you want to run things in Play mode in this Importer project, the physics and collision behaviour should match what you'll see in the game. Then, fill out the 
			Species Name and select the Body Orientation for your character. For the Character Model field, drag and drop your character model from the Project window. Then, click Initialize to 
			create the Character Importer instance in your scene.
		</p>
        
		<b-img src="/images/importer/model-export-import/importer-selected.jpg"></b-img>
		<p>
			If you click on the newly created CharacterImporter object in the Hierarchy, you can see the importer component in the Inspector, which is where the majority of setup work will be 
			done. Those modules will be covered in other guides.
		</p>
	</div>
</template>

<script>
export default {
		name: 'ModelExportAndImport',
}
</script>
